import { useCallback, useEffect, useMemo, useState } from "react";
import { IconArrowsVertical, IconCircleCheck, IconExternalLink } from "@tabler/icons-react";
import { axiosInstance } from "../../utils/axiosInstance";
import { OneUptimeDegradedMonitor } from "../../interfaces/interfaces";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  EmptyState,
  ErrorBanner,
  LoadingPanel,
  PageHeader,
  Pill,
  RefreshButton,
} from "../ui";

function sortByName(
  list: OneUptimeDegradedMonitor[],
  ascending: boolean
): OneUptimeDegradedMonitor[] {
  return list.slice().sort((a, b) => {
    const cmp = a.name.localeCompare(b.name);
    if (cmp !== 0) return ascending ? cmp : -cmp;
    return String(a.id).localeCompare(String(b.id));
  });
}

export default function DegradedMonitorsHome() {
  const [monitors, setMonitors] = useState<OneUptimeDegradedMonitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [nameAscending, setNameAscending] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const res = await axiosInstance.get<{
        monitors: OneUptimeDegradedMonitor[];
        message?: string;
      }>("/monitoring/degraded-monitors");
      const list = res.data.monitors || [];
      setMonitors(list);
      if (res.data.message && list.length === 0) {
        setInfo(res.data.message);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const status = err?.response?.status;
      if (status === 400 && Array.isArray(data?.monitors)) {
        setMonitors(data.monitors);
        setInfo(data.message || null);
        setError(null);
      } else if (status === 503 && Array.isArray(data?.monitors)) {
        setMonitors(data.monitors);
        setInfo(data.message || null);
        setError(null);
      } else {
        const msg =
          data?.message ||
          err?.message ||
          "Failed to load degraded monitors.";
        setError(msg);
        setMonitors([]);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of monitors) {
      if (row.status) set.add(String(row.status));
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [monitors]);

  const display = useMemo(() => {
    const filtered = monitors.filter((row) => {
      if (statusFilter && String(row.status ?? "") !== statusFilter) {
        return false;
      }
      return true;
    });
    return sortByName(filtered, nameAscending);
  }, [monitors, statusFilter, nameAscending]);

  const filtersActive = Boolean(statusFilter);

  return (
    <PageContainer>
      <PageHeader
        title="Degraded monitors"
        subtitle="Degraded or offline monitors from OneUptime"
        actions={<RefreshButton onClick={load} loading={loading} />}
      />

      {!loading && error ? <ErrorBanner onRetry={load}>{error}</ErrorBanner> : null}
      {!loading && !error && info && monitors.length === 0 ? (
        <div className="alert alert-info" role="alert">
          {info}
        </div>
      ) : null}

      <Card flush>
        {!loading && !error && monitors.length > 0 ? (
          <div className="sq-toolbar">
            <label className="sq-field">
              <span>Status</span>
              <select
                className="sq-input"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All</option>
                {statusOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <span className="sq-count sq-toolbar-end">
              Showing {display.length} of {monitors.length}
            </span>
          </div>
        ) : null}

        {loading ? <LoadingPanel label="Loading degraded monitors" rows={3} /> : null}

        {!loading && !error && monitors.length === 0 && !info ? (
          <EmptyState icon={<IconCircleCheck size={24} />} title="All monitors operational">
            No degraded or offline monitors in OneUptime.
          </EmptyState>
        ) : null}

        {!loading && !error && monitors.length > 0 && display.length === 0 ? (
          <EmptyState
            title="No monitors match this filter"
            action={
              filtersActive ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setStatusFilter("")}
                >
                  Clear filter
                </button>
              ) : undefined
            }
          />
        ) : null}

        {!loading && !error && display.length > 0 ? (
          <div className="sq-table-wrap">
            <table className="sq-table is-stacked">
              <thead>
                <tr>
                  <th scope="col" aria-sort={nameAscending ? "ascending" : "descending"}>
                    <button
                      type="button"
                      className="sortable"
                      onClick={() => setNameAscending((prev) => !prev)}
                    >
                      Name
                      <IconArrowsVertical size={14} stroke={1.75} />
                    </button>
                  </th>
                  <th scope="col">Status</th>
                  <th scope="col">ID</th>
                </tr>
              </thead>
              <tbody>
                {display.map((row) => {
                  const href =
                    typeof row.externalUrl === "string" && row.externalUrl ? row.externalUrl : null;
                  return (
                    <tr key={row.id}>
                      <td className="cell-title sq-strong">
                        {href ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sq-text-link d-inline-flex align-items-center gap-1"
                            title="Open in OneUptime"
                          >
                            {row.name}
                            <IconExternalLink size={14} stroke={1.75} />
                          </a>
                        ) : (
                          row.name
                        )}
                      </td>
                      <td data-label="Status">
                        {row.status ? (
                          <Pill tone={/offline|down/i.test(row.status) ? "bad" : "warn"}>
                            {row.status}
                          </Pill>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td data-label="ID">
                        <span className="sq-mono">{row.id}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </PageContainer>
  );
}
